import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";

import "./DownloadAndroidAppModal.scss";
import { GenericModalProps } from "../../utils/constants/generic-types";
import useDisclosure from "../../hooks/useDisclosure";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import ImageAndroidIcon from "../../images/android-icon.svg";
import ImageDownloadAnywayDialog from "../../images/download-anyway.png";
import ImageDownloadLocation from "../../images/download-tutorial.gif";
import ImageHyper from "../../images/hyper.svg";
import ImageActivePlayers from "../../images/active-players.svg";
import ImageFiatCashStack from "../../images/fiat-cash-stack.svg";
import ImageCheckmark from "../../images/checkmark-brand.svg";
import CustomModal from "../CustomModal/CustomModal";
import { useDownloadApp } from "../../hooks/useDownloadApp";
import useAuth from "../../hooks/useAuth";
import { ROUTES } from "../../utils/constants";
import XIcon from "../icons/XIcon";
import QuestionMarkIcon from "../icons/QuestionMarkIcon";

enum DOWNLOAD_PHASE {
  BEFORE_DOWNLOAD = "before",
  AFTER_DOWNLOAD = "after",
}

interface BeforeDownloadProps {
  openInfoDialog: () => void;
  changeDownloadPhase: () => void;
}

function WhyWeUseAnApk({ isOpen, onClose }: GenericModalProps) {
  const { t } = useTranslation("common");

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      showCloseBtn={false}
      headingClass="d-none"
      containerClass="WhyWeUseAnApk"
      backdropClassName="WhyWeUseAnApkBackdrop"
    >
      <div className="WhyWeUseAnApkContent">
        <h2>{t("download_android.heres_why_we_use_apk")}</h2>
        <div className="body">
          <p>{t("download_android.play_store_policy")}</p>
          <p>{t("download_android.official_website")}</p>
          <p>{t("download_android.available_on_ios")}</p>
        </div>
        <PrimaryActionButton
          size="lg"
          text={t("download_android.got_it")}
          onClick={onClose}
        />
      </div>
    </BottomDrawer>
  );
}

function DownloadStatCard({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
}) {
  return (
    <div className="download-stat-card">
      <span className="icon">{icon}</span>
      <span className="text">
        <span className="value">{value}</span>
        <span className="label">{label}</span>
      </span>
    </div>
  );
}

function OfficialBuildBadge({ version }: { version?: number }) {
  const { t } = useTranslation("common");

  if (version == null) {
    return null;
  }

  return (
    <div className="official-build-badge">
      <span className="icon">
        <img src={ImageHyper} alt="" width={24} height={24} />
      </span>
      <span className="text">
        <span className="label">
          {t("download_android.official_android_build")}
        </span>
        <span className="version">
          {t("download_android.app_version", { version })}
        </span>
      </span>
    </div>
  );
}

function BeforeDownload({
  openInfoDialog,
  changeDownloadPhase,
  closeModal,
}: BeforeDownloadProps & {
  closeModal: GenericModalProps["onClose"];
}) {
  const { apk } = useDownloadApp();
  const { t } = useTranslation("common");

  return (
    <div className="BeforeDownload">
      <div className="main">
        <div className="header">
          <button
            type="button"
            className="header-btn"
            aria-label={t("close")}
            onClick={closeModal}
          >
            <XIcon />
          </button>
          <button
            type="button"
            className="header-btn"
            aria-label={t("download_android.help")}
            onClick={openInfoDialog}
          >
            <QuestionMarkIcon />
          </button>
        </div>
        <h1>{t("download_android.title")}</h1>
        <p className="explainer">{t("download_android.body")}</p>
        <div className="instruction-card">
          <p className="instruction">
            <Trans
              t={t}
              i18nKey="download_android.instruction"
              components={{ bold: <strong /> }}
            />
          </p>
          <Image
            src={ImageDownloadAnywayDialog}
            alt={t("download_android.download_anyway_image_alt")}
            width={270}
            height={140}
            className="dialog-image"
          />
        </div>
        <div className="stats">
          <DownloadStatCard
            icon={
              <img src={ImageActivePlayers} alt="" width={32} height={32} />
            }
            value={t("download_android.active_players_count")}
            label={t("download_android.active_players")}
          />
          <DownloadStatCard
            icon={
              <img src={ImageFiatCashStack} alt="" width={32} height={29} />
            }
            value={t("download_android.winnings_paid_amount")}
            label={t("download_android.winnings_paid")}
          />
        </div>
      </div>
      <div className="footer">
        <OfficialBuildBadge version={apk.version} />
        <PrimaryActionButton
          href={apk.url}
          size="lg"
          text={t("download_android.download_apk_cta")}
          leftImage={ImageAndroidIcon}
          imgClassName="AndroidIcon"
          buttonClassName="download-cta"
          onClick={changeDownloadPhase}
        />
      </div>
    </div>
  );
}

function AfterDownload({
  closeModal,
}: {
  closeModal: GenericModalProps["onClose"];
}) {
  const { isLoggedIn } = useAuth();
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    closeModal();
    if (!isLoggedIn) {
      navigate(ROUTES.ROOT.LOG_IN);
    }
  }, [closeModal, isLoggedIn, navigate]);

  return (
    <div className="AfterDownload">
      <div className="main">
        <div className="download-started-toast">
          <img src={ImageCheckmark} alt="" width={20} height={20} />
          <span>{t("download_android.download_started")}</span>
        </div>
        <h1>{t("download_android.hyper_is_downloading")}</h1>
        <ol className="instructions">
          <li>
            <span className="count">1</span>
            <span className="text">
              {t("download_android.wait_for_download")}
            </span>
          </li>
          <li>
            <span className="count">2</span>
            <span className="text">
              {t("download_android.open_download_folder")}
            </span>
          </li>
          <li>
            <span className="count">3</span>
            <span className="text">
              {t("download_android.open_hyper_and_start_winning")}
            </span>
          </li>
        </ol>
        <div className="download-location">
          <Image
            src={ImageDownloadLocation}
            alt={t("download_android.download_location_alt")}
          />
        </div>
      </div>
      <PrimaryActionButton
        size="lg"
        text={t("continue")}
        buttonClassName="w-100"
        onClick={handleClick}
      />
    </div>
  );
}

export default function DownloadAndroidAppModal({
  isOpen,
  onClose,
}: GenericModalProps) {
  const [downloadPhase, setDownloadPhase] = useState<DOWNLOAD_PHASE>(
    DOWNLOAD_PHASE.BEFORE_DOWNLOAD,
  );
  const {
    isOpen: isInfoDialogOpen,
    onOpen: openInfoDialog,
    onClose: closeInfoDialog,
  } = useDisclosure();

  const changeDownloadPhase = (newPhase: DOWNLOAD_PHASE) => {
    setDownloadPhase(newPhase);
  };

  function handleStartDownload() {
    changeDownloadPhase(DOWNLOAD_PHASE.AFTER_DOWNLOAD);
  }

  const handleCloseModal = () => {
    onClose();
    setTimeout(() => changeDownloadPhase(DOWNLOAD_PHASE.BEFORE_DOWNLOAD), 1000);
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        isFullscreen
        showCloseBtn={false}
        customClass="DownloadAndroidAppModalContainer"
      >
        <div className="h-100 d-flex flex-column align-items-center justify-content-between DownloadAndroidAppModal">
          {downloadPhase === DOWNLOAD_PHASE.BEFORE_DOWNLOAD && (
            <BeforeDownload
              openInfoDialog={openInfoDialog}
              changeDownloadPhase={handleStartDownload}
              closeModal={handleCloseModal}
            />
          )}
          {downloadPhase === DOWNLOAD_PHASE.AFTER_DOWNLOAD && (
            <AfterDownload closeModal={handleCloseModal} />
          )}
        </div>
      </CustomModal>

      <WhyWeUseAnApk isOpen={isInfoDialogOpen} onClose={closeInfoDialog} />
    </>
  );
}
